import axios from "axios";
import { setupCache } from "axios-cache-interceptor";
import store from "../redux-store/store";
import { logoutUser } from "redux-store/auth/actions";
import { handleUnAuthorizedAction } from "redux-store/auth/reducers";
import { getActingOrgId, isSuperAdmin } from "utils/abacRoles";
import { decide401, registerSessionCheck, registerSessionRenewal } from "./session";
import { replayWithCurrentToken } from "./replay";
import { refreshSession } from "utils/session";
import { describeNoResponse } from "./networkError";
import { resolveTenantSubdomain } from "config/tenancy";
import { clearSharedAuthToken } from "../utils/sharedAuthCookie";

function getSubdomain() {
  if (typeof window === "undefined") {
    return null;
  }

  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  if (parts.length > 2) {
    return parts[0];
  }
  return null;
}

const baseURL = process.env.REACT_APP_API_URL;
const headers = {
  "Content-Type": "application/json",
};
export const requestInterceptors = [
  (config) => {
    if (localStorage.authToken) config.headers.Authorization = `Bearer ${localStorage.authToken}`;
    // On a non-tenant host (backoffice, app, account, www) the tenant stored
    // from the login response wins — see config/tenancy.
    const subdomain = resolveTenantSubdomain(getSubdomain(), localStorage._subdomain);
    if (subdomain) config.headers._subdomain = `${subdomain}`;

    // Add organization ID for defense-in-depth tenant isolation
    const authState = store.getState()?.auth;
    const orgId = authState?.user?.organizationId ?? authState?.user?.userDetails?.organizationId;
    if (orgId) {
      config.headers['X-Organization-Id'] = orgId.toString();
    }

    // Super-admin tenant switcher: only attach X-Acting-Org-Id when the
    // current user is a super admin AND has selected an org in the
    // platform-ABAC TenantSwitcher. Backend ActingOrgMiddleware will
    // silently ignore the header for non-platform-admin subjects, but
    // we still gate on the frontend to avoid sending a useless header
    // (and to keep regular tenant requests free of the extra metadata).
    const actingOrgId = getActingOrgId();
    if (actingOrgId && isSuperAdmin(authState?.user)) {
      config.headers["X-Acting-Org-Id"] = actingOrgId.toString();
    }

    return config;
  },
  (error) => Promise.reject(error),
];
function signOut() {
  localStorage?.clear();
  // localStorage.clear() does not touch cookies; without this the dead token
  // is re-seeded from the parent domain on the next load.
  clearSharedAuthToken();
  store.dispatch(logoutUser());
  store.dispatch(handleUnAuthorizedAction(true));
}

// Only flex2-api's /auth/user-info is the session authority. lcp, afms, files
// and the other services reuse these interceptors, and a 401 from one of them
// can mean "I couldn't reach flex2-api to resolve you" rather than "your token
// is dead". Signing out on those kicked inspectors out mid-inspection. The
// same goes for flex2-api's own endpoints: a presence heartbeat 401'd minutes
// after a fresh login in prod. So any 401 other than user-info's is first
// checked against user-info, once, shared by every request that failed at the
// same moment (see ./session).
let sessionCheck = null;
function confirmSessionDead() {
  if (!sessionCheck) {
    sessionCheck = axios
      // Same token / _subdomain / org headers as any flex2-api call.
      .post(`${baseURL}/auth/user-info`, {}, requestInterceptors[0]({ headers: { ...headers } }))
      .then(() => false)
      .catch((e) => e?.response?.status === 401)
      .finally(() => {
        setTimeout(() => {
          sessionCheck = null;
        }, 5000);
      });
  }
  return sessionCheck;
}

registerSessionCheck(confirmSessionDead);
registerSessionRenewal(refreshSession);

export const responseInterceptors = [
  (result) => {
    return result;
  },
  async (error) => {
    if (error?.response?.status === 401) {
      const verdict = await decide401(error);
      if (verdict === "retry") {
        // The session was renewed (here or by a concurrent request) — replay
        // with the new token instead of failing the call.
        return replayWithCurrentToken(error.config);
      }
      if (verdict === "signout") {
        signOut();
      } else {
        console.warn(
          `[API] 401 from ${error?.config?.baseURL}${error?.config?.url} but /auth/user-info still accepts the session — not signing out.`
        );
      }
    } else if (error?.response?.status === 403) {
      console.warn(
        `[API] 403 Forbidden: ${error?.config?.method?.toUpperCase()} ${error?.config?.url}`,
        error?.response?.data?.message || ""
      );
    } else if (error.code === "ERR_CANCELED") {
      return Promise.resolve({ status: 499 });
    }
    describeNoResponse(error);
    return Promise.reject(error);
  },
];

const apiInstance = axios.create({
  baseURL,
  headers,
});

apiInstance.interceptors.request.use(...requestInterceptors);

apiInstance.interceptors.response.use(...responseInterceptors);

export const apiInstanceFileUpload = axios.create({
  baseURL,
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

apiInstanceFileUpload.interceptors.request.use(...requestInterceptors);

apiInstanceFileUpload.interceptors.response.use(...responseInterceptors);

const cachedApi = axios.create({
  baseURL,
  headers,
});

cachedApi.interceptors.request.use(...requestInterceptors);

export const cachedApiInstance = setupCache(cachedApi);

cachedApi.interceptors.response.use(...responseInterceptors);

export default apiInstance;
